import React, { useEffect, useState, FC, useRef } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import * as types from "../gql/graphql";

import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import Container from '@mui/material/Container';
import AddBoxIcon from '@mui/icons-material/AddBox';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Switch from "@mui/material/Switch";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from '@mui/material/MenuItem';

import MDBox from './MDBox/MDBoxRoot';
import setThemeObj from './../hooks/useThemeMUI';
import MDTypography from './MDTypography/MDTypographyRoot';
import MDButton from './MDButton/MDButtonRoot';
import MDInput from './MDInput/MDInputRoot';

import { SubmitErrorHandler, SubmitHandler, useForm, Controller } from 'react-hook-form';
import { watch } from 'fs';
import { duration } from '@mui/material';
import { useQueryAll } from '../apolloClient';

type ClassItem = {
    className: string;
    classId: number;
    classRep: number;
  };

  function CompareListsOfID(oldList: ClassItem[], newList: ClassItem[]) {
    const addClasses: ClassItem[] = [];
    const deleteIDs: number[] = [];
  
    // Create a map of classIds and classReps for easier lookup
    const oldListMap = new Map(oldList.map(obj => [obj.classId, obj.classRep]));
    const newListMap = new Map(newList.map(obj => [obj.classId, obj.classRep]));
  
    // Find additions by checking which class IDs are in newList but not in oldList, or have changed repetitions
    newList.forEach(obj => {
      const oldRep = oldListMap.get(obj.classId);
      if (!oldListMap.has(obj.classId) || (oldRep !== undefined && oldRep !== obj.classRep)) {
        addClasses.push(obj); // Add class with changed repetitions or new class
      }
    });
  
    // Find deletions by checking which class IDs are in oldList but not in newList, or have changed repetitions
    oldList.forEach(obj => {
      const newRep = newListMap.get(obj.classId);
      if (!newListMap.has(obj.classId) || (newRep !== undefined && newRep !== obj.classRep)) {
        deleteIDs.push(obj.classId); // Treat class with changed repetitions as deleted
      }
    });
  
    return { addClasses, deleteIDs };
  }


interface EditPackageDialogProps {
    rowData: any; // Adjust the type according to your data structure
    //render: (open: any) => any;
    onSave: () => void;
    onClose: () => void;
}


const getProductCategoriesQuery = gql(`query GetProductCategories {
    fs_prodcategoryCollection {
        edges {
          node {
            nodeId
            id
            catname
          }
        }
      }
  }`);

const getClassesForPackagesQuery = gql(`
  query GetClassesForPackages($after: Cursor) {
    fs_classesCollection(after: $after) {
        pageInfo { endCursor startCursor hasNextPage } 
        edges {
          node {
            nodeId
            id
            name
          }
        }
      }
  }
`);

// TODO: Reconcile Form with Dialog, is Diolog needed? 
function Form(rowData: any, onSave, onClose) {

    const { data: PCdata } = useQuery(getProductCategoriesQuery);

    const { data: Cdata } = useQueryAll<types.Fs_ClassesEdge>({query: getClassesForPackagesQuery});

//    const classes = Cdata?.map(item => item.node);

    // If row is missing add an empty one for addition
    rowData = rowData || { listorder: 1000, id: -1, eventtype: 0, category: 0, classes: null, active: true };

    type PackageFormType = types.Fs_PackagesUpdateInput & { classes: number };

    const { register,
        control,
        handleSubmit,
        clearErrors,
        getValues,
        reset,
        watch,
        formState: { errors } } = useForm<PackageFormType>({
            mode: 'onChange',
            defaultValues: rowData
        })


    const savePackageDataQuery = gql(`
        mutation savePackageDataQuery($cl: fs_packagesUpdateInput!, $filter:fs_packagesFilter) {
            updatefs_packagesCollection(set: $cl, filter: $filter) {
                affectedCount
                records {
                    name
                }
            }
        }      
        `);

    const [savePackageMutation, { data, loading, error }] = useMutation(savePackageDataQuery, {
        refetchQueries: [
            'GetPackages' // Query name
        ],
    });

    const addPackageDataQuery = gql(`
    mutation addPackageDataQuery($name: String!, $description: String!, $category: Int!, $active: Boolean!, $code: String!, $listorder: Int!, $externalId: String!, $class_ids: [BigInt]!, $class_repetitions: [Int]!) {
        add_package(
          p_name: $name
          p_description: $description
          p_category: $category
          p_active: $active
          p_code: $code
          p_listorder: $listorder
          p_externalid: $externalId
          p_class_ids: $class_ids
          p_repetitions: $class_repetitions
        )
      }       
    `);

    const [addPackageMutation, { data: add_data, loading: add_loading, error: add_error }] = useMutation(addPackageDataQuery, {
        refetchQueries: [
            'GetPackages' // Query name
        ],
    });


    const [packageList, setPackageList] = useState<ClassItem[]>(null);
    const [saving, setSaving] = useState(false);
    const [selectedClassID, setSelectedClassID] = useState(null);
    const [selectedClassValue, setSelectedClassValue] = useState(null);    
 //   const [selectedClassName, setSelectedClassName] = useState(null);
    const classesData = Cdata;

    // Package classes queries
    const getClassesInPackageQuery = gql(`
    query GetClassesInPackage($filter:fs_packageclassesFilter) {
        fs_packageclassesCollection(filter: $filter) {
      		edges {
            node {
              nodeId
              package
              class
              fs_classes {nodeId id name}
              repetitions
            }
          }
        }
    }
    `);
    const { data: CinPdata } = useQuery(getClassesInPackageQuery, { variables: { filter: { package: { eq: rowData.id } } } });
    // { "filter": {"package": {"eq": 9}}}


    // Initilize list of classes
    useEffect(() => {
        if (CinPdata) {
            const classes : ClassItem[] = [];
            CinPdata.fs_packageclassesCollection.edges.map(r => {
                classes.push({ className: r.node.fs_classes.name, classId: r.node.fs_classes.id, classRep: r.node.repetitions });
            });
            setPackageList(classes);
        }
    }, [CinPdata]);


    const addClassesToPackageQuery = gql(`
        mutation addClassesToPackage($cinp: [fs_packageclassesInsertInput!]! ) {
            insertIntofs_packageclassesCollection(objects:$cinp) {
                affectedCount
            }
        }  
    `);

    const [addClassesToPackageMutation, { called: addCalled, loading: addLoading }] = useMutation(addClassesToPackageQuery, {
        refetchQueries: [
            'GetClassesInPackage' // Query name
        ], awaitRefetchQueries: true
    });

    const deleteClassInPackageQuery = gql(`
        mutation deleteClassesFromPackageQuery($filter:fs_packageclassesFilter) {
            deleteFromfs_packageclassesCollection(filter: $filter, atMost: 20) {
                affectedCount
            }
        }
    `);

    const [deleteClassesMutation, { called: delCalled, loading: delLoading }] = useMutation(deleteClassInPackageQuery, {
        refetchQueries: [
            'GetClassesInPackage' // Query name
        ], awaitRefetchQueries: true
    });

    if (saving) {
        if ((!addLoading) && (!delLoading)) onSave();
    }

    const submit: SubmitHandler<PackageFormType> = async (data) => {

        // Now filtering extra fields here. Need to find a better way to do that. 
        // Will using state management library help? Redux?
        function pickObjectKeys<T, K extends keyof T>(obj: T, keys: K[]) {
            let result = {} as Pick<T, K>
            for (const key of keys) {
                if (obj.hasOwnProperty(key)) {
                    result[key] = obj[key]
                }
            }
            return result;
        }

        // TODO: provide default value for listorder. Where?
        const filtered_data = pickObjectKeys(data, ['active', 'code', 'description', 'listorder', 'name', 'category']);

        if (rowData.id == -1) {
            const class_ids = packageList.map(obj => obj.classId);
            const class_reps = packageList.map(obj => obj.classRep);
            await addPackageMutation({
                variables: {
                    name: filtered_data.name,
                    description: filtered_data.description,
                    category: filtered_data.category,
                    active: filtered_data.active ? true : false,
                    code: filtered_data.code,
                    listorder: filtered_data.listorder,
                    externalId: "",
                    class_ids: class_ids,
                    class_repetitions: class_reps
                }
            });
        }
        else {
            savePackageMutation({ variables: { filter: { nodeId: { eq: rowData.nodeId } }, cl: filtered_data } });

            // Save package classes if any
            const origClasses: ClassItem[] = [];
            if (CinPdata) {
                CinPdata.fs_packageclassesCollection.edges.map(r => {
                    origClasses.push({ className: r.node.fs_classes.name, classId: r.node.fs_classes.id, classRep: r.node.repetitions });
                });
            }
            const changes = CompareListsOfID(origClasses, packageList);

            if (changes.deleteIDs.length) {
                await deleteClassesMutation({ variables: { filter: { and: { class: { in: changes.deleteIDs }, package: { eq: rowData.id } } } } });
            }

            if (changes.addClasses.length) {
                const additions = [];
                changes.addClasses.map(additionalClass => { additions.push({ class: additionalClass.classId, package: rowData.id, repetitions: additionalClass.classRep }) });
                await addClassesToPackageMutation({ variables: { cinp: additions } });
            }
        }

        setSaving(true);
    }

    const errorHandler: SubmitErrorHandler<types.Fs_PackagesUpdateInput> = data => {
        console.log(data + ' 2!Error');
    }

    const handleSelectChange = (event) => {
        console.log('event.target.value', event.target.value);
        setSelectedClassID(event.target.value);
        let selectedName = "";
        for (const item of classesData) {
            if (item.node.id === event.target.value) {
                selectedName = item.node.name;
                break;
            }
        }
        const newSelectedOption = { id: event.target.value, name: selectedName };
        setSelectedClassValue(newSelectedOption);
    };

    const handleAddValue = () => {
        if (selectedClassID) {
            const isDuplicate = packageList.findIndex(option => option.classId == selectedClassID) != -1;
            if (!isDuplicate) {
                setPackageList([...packageList, {classId: selectedClassValue.id, className: selectedClassValue.name, classRep: 1}]);
            }
        }
    };

    const handleRemoveValue = (index) => {
        const updatedOptions = [...packageList];
        updatedOptions.splice(index, 1);
        setPackageList(updatedOptions);
    };

    const handleCountChange = (index: number, value: string) => {
        const newPackageList = [...packageList];
        newPackageList[index] = { ...newPackageList[index], classRep: Number(value) };
        setPackageList(newPackageList);
      };   

    //console.log('watch', watch);
    return (
        <MDBox theme={setThemeObj}
            component="main" display="flex"
            justifyContent="space-between"
            alignItems="baseline"
            flexDirection="column"
            pt={2}
            pb={0.5}
            px={2}
            sx={{ flexGrow: 1, width: "100%" }}
            width="100%"
        >
            <form onSubmit={handleSubmit(submit, errorHandler)} id="testForm">
                { /* TODO: Update validation rules for all fields.   */}
                <MDBox component="table">
                    <MDBox component="tbody">
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Name</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput {...register('name', { required: true })}
                                    variant="standard"
                                    size="medium"
                                    fullWidth
                                    aria-invalid={errors.name ? true : false} />
                                {errors.name && <MDBox><MDTypography variant="h6" px={1} ownerState={{ color: "error" }}>This field is required</MDTypography></MDBox>}
                            </MDBox>
                            <MDBox px={1} component="td"></MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Description</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput {...register('description')}
                                    variant="standard"
                                    size="medium"
                                    fullWidth />
                            </MDBox>
                            <MDBox px={1} component="td"></MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Active?</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                            <Controller
                                    name="active"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Checkbox checked={value} onChange={onChange} />                                          
                                      )}
                                    />
                            </MDBox>
                            <MDBox px={1} component="td"></MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Category</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    {...register('category', { valueAsNumber: true })}
                                    defaultValue={rowData?.category || "0"}
                                    select
                                    variant="standard"
                                    fullWidth>
                                    <MenuItem value="0">Select Category</MenuItem>
                                    {PCdata?.fs_prodcategoryCollection.edges.map((item) => { return <MenuItem value={item.node.id} key={item.node.id}>{item.node.catname}</MenuItem> })}
                                </MDInput>
                            </MDBox>
                            <MDBox px={1} component="td"></MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Code</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput {...register('code', { required: true })}
                                    variant="standard"
                                    size="medium"
                                    fullWidth
                                    aria-invalid={errors.code ? true : false} />
                                {errors.code && <MDBox><MDTypography variant="h6" px={1} ownerState={{ color: "error" }}>This field is required</MDTypography></MDBox>}
                            </MDBox>
                            <MDBox px={1} component="td"></MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Order in schedule</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    {...register('listorder', { valueAsNumber: true, required: true })}
                                    type='number'
                                    variant="standard"
                                    size="medium"
                                    fullWidth
                                    aria-invalid={errors.listorder ? true : false} />
                                {errors.listorder && <MDBox><MDTypography variant="h6" px={1} ownerState={{ color: "error" }}>This field is required</MDTypography></MDBox>}
                            </MDBox>
                            <MDBox px={1} component="td"></MDBox>
                        </MDBox>


                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td">
                                <MDTypography variant="h4" px={1} ownerState={{ color: "dark" }}>Classes</MDTypography>
                            </MDBox>
                            <MDBox px={1} sx={{ width: "100%" }} component="td">
                                <MDInput
                                    {...register('classes', { valueAsNumber: true })}
                                    onChange={handleSelectChange}
                                    value={selectedClassID ? selectedClassID : 0}
                                    select
                                    variant="standard"
                                    fullWidth>
                                    <MenuItem value="0">Select Classes</MenuItem>
                                    {Cdata?.map((item) => { return <MenuItem value={item.node.id} key={item.node.id}>{item.node.name}</MenuItem> })}
                                </MDInput>
                            </MDBox>
                            <MDBox px={1} component="td">
                                <MDButton
                                    startIcon={<AddBoxIcon />}
                                    size="large"
                                    ownerState={{ color: "info", iconOnly: true }}
                                    onClick={handleAddValue} />
                            </MDBox>
                        </MDBox>
                        <MDBox lineHeight={1}
                            alignItems="center"
                            component="tr"
                            sx={{ verticalAlign: "middle" }}>
                            <MDBox px={1} component="td"></MDBox>
                            <MDBox px={1} component="td">
                                <MDBox component="table">
                                    <MDBox component="tbody">
                                        {packageList?.map((option, index) => (<>

                                            <MDBox px={1} key={index} component="tr">
                                                <MDBox px={1} component="td" sx={{ width: "70%" }}>
                                                    <MDTypography variant="h5" px={1} ownerState={{ color: "dark" }}>{option.className}
                                                    </MDTypography>
                                                </MDBox>
                                                <MDBox px={1} component="td" sx={{ width: "30%" }}>
                                                    <MDInput
                                                        type='number'
                                                        variant="standard"
                                                        size="medium"
                                                        fullWidth
                                                        value={option.classRep}
                                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleCountChange(index, event.target.value)}
                                                    />
                                                </MDBox>
                                                <MDBox px={1} component="td">
                                                    <MDButton
                                                        startIcon={<HighlightOffIcon />}
                                                        size="large"
                                                        name='newclassInput'
                                                        ownerState={{ color: "error", iconOnly: true }}
                                                        onClick={() => handleRemoveValue(index)} aria-label="add" />
                                                </MDBox>
                                            </MDBox>
                                        </>
                                        ))}
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        </MDBox>

                    </MDBox>
                </MDBox>

                <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="baseline"
                    flexDirection="row"
                    gap="16px"
                    pt={2}
                    pb={0.5}
                    px={2}
                    width="100%"
                >
                    <MDButton
                        ownerState={{
                            variant: "gradient",
                            color: "info",
                            textTransform: "none",
                            circular: false,
                            iconOnly: false,
                            darkMode: false
                        }}
                        size="medium"
                        type="submit">
                        Submit </MDButton>
                    <MDButton
                        size="medium"
                        onClick={() => onClose()}
                        ownerState={{ textTransform: "none", variant: "gradient", color: "warning", }}>
                        Cancel </MDButton>
                </MDBox>
            </form>
        </MDBox>
    )
}


const EditPackageDialog: FC<EditPackageDialogProps> = ({ rowData, onSave, onClose }) => {


    // Your modal content and form for editing the row data

    // Is it needed?
    const handleEntered = () => {
        // The `autoFocus` is not used because, if used, the same Enter that saves
        // the cell triggers "No". Instead, we manually focus the "No" button once
        // the dialog is fully open.
        // noButtonRef.current?.focus();
    };

    const noButtonRef = React.useRef<HTMLButtonElement>(null);

    // Do we need dialog?
    return (
        <Dialog
            maxWidth="sm"
            TransitionProps={{ onEntered: handleEntered }}
            open={true}
            fullWidth
        >
            <MDTypography variant="h5" px={1} py={1}>Save packege? <CloseIcon onClick={onClose} sx={{ position: "absolute", top: "10px", right: "10px" }} /></MDTypography>
            <DialogContent dividers>
                {Form(rowData, onSave, onClose)}
            </DialogContent>
        </Dialog>
    );
};


export default EditPackageDialog;
